














import { DEFAULT_DATE_FORMAT } from "@/models/constants/date.constant";
import {
  CashFlowCategoryAmount,
  CashFlowColumnResult,
  CashFlowReportSectionTableRow,
  CashFlowReportTableRow,
  CashFlowSectionResult,
  ColumnConfig,
} from "@/models/interface/cash-flow-report/CashFlowReport.interface";
import { currencyFormat } from "@/validator/globalvalidator";
import moment from "moment";
import { VNode } from "vue";
import { Component, Emit, Prop, Vue } from "vue-property-decorator";

const ROW_CONFIG = {
  byType: {
    section: "cash-flow-report__header",
    subtotal: "cash-flow-report__subtotal",
    cashflow: "cash-flow-report__footer",
    "opening-balance": "cash-flow-report__footer",
    "closing-balance": "cash-flow-report__footer",
    category: "cash-flow-report__category",
  },
  footerTypes: ["cashflow", "opening-balance", "closing-balance"],
} as const;

@Component
export default class CashFlowReportTable extends Vue {
  @Prop({ type: Array, default: () => [] })
  dataSource!: CashFlowColumnResult[];

  @Prop({ type: Boolean, default: false })
  loading!: boolean;

  @Prop({ type: Boolean, default: false })
  showTotal!: boolean;

  defaultDateFormat = DEFAULT_DATE_FORMAT;
  currencyFormat = currencyFormat;
  moment = moment;
  ROW_CONFIG = ROW_CONFIG;

  private createStyledElement(content: string, className: string): VNode {
    return this.$createElement("p", { class: className }, content);
  }

  private formatAmount(value: string | number | null | undefined): string {
    const formatted = this.currencyFormat(value);
    if (formatted.startsWith("-")) {
      return `(${formatted.slice(1)})`;
    }
    return formatted;
  }

  private sumPeriodValues(row: CashFlowReportTableRow): number | null {
    let sum = 0;
    let found = false;
    for (let index = 0; index < this.dataSource.length; index++) {
      const value = row[`period_${index}`];
      if (value !== null && value !== undefined && value !== "") {
        sum += Number(value);
        found = true;
      }
    }
    return found ? sum : null;
  }

  private createClickableAmount(
    text: string | number,
    record: CashFlowReportTableRow,
    period: CashFlowColumnResult
  ): VNode {
    const isClickable = record.type === "category" && !!record.categoryId;
    const formatted = text !== null ? this.formatAmount(text) : "-";
    const className = `cash-flow-report__amount ${
      isClickable
        ? "cash-flow-report__amount--clickable"
        : "cash-flow-report__amount--default"
    }`;

    if (isClickable) {
      return this.$createElement(
        "a",
        {
          class: className,
          on: {
            click: () =>
              this.handleDetailClick({
                categoryId: record.categoryId as string,
                dateFrom: period.dateFrom,
                dateTo: period.dateTo,
              }),
          },
        },
        formatted
      );
    }

    return this.$createElement("span", { class: className }, formatted);
  }

  @Emit("detail-click")
  handleDetailClick(payload: {
    categoryId: string;
    dateFrom: string;
    dateTo: string;
  }): {
    categoryId: string;
    dateFrom: string;
    dateTo: string;
  } {
    return payload;
  }

  get totalColumnCount(): number {
    return 1 + (this.dataSource?.length || 0) + (this.showTotal ? 1 : 0);
  }

  get activityLabels(): Record<string, string> {
    return {
      OPERATING: this.$t("lbl_operating_activities").toString(),
      INVESTING: this.$t("lbl_investing_activities").toString(),
      FINANCING: this.$t("lbl_financing_activities").toString(),
    };
  }

  get getColumns(): ColumnConfig<CashFlowReportTableRow>[] {
    const columns: ColumnConfig<CashFlowReportTableRow>[] = [
      {
        title: "Category",
        dataIndex: "label",
        key: "category",
        customHeaderCell: () => ({ class: "cash-flow-report__th--lg" }),
        customCell: (record: CashFlowReportTableRow) => {
          if (record.type === "section") {
            return { class: "cash-flow-report__cell--header" };
          }
          if (
            ROW_CONFIG.footerTypes.includes(
              record.type as "cashflow" | "opening-balance" | "closing-balance"
            )
          ) {
            return { class: "cash-flow-report__cell--footer" };
          }
          return {};
        },
        customRender: (_text: string, record: CashFlowReportTableRow) => {
          const className =
            ROW_CONFIG.byType[record.type] || ROW_CONFIG.byType.category;
          const label = this.createStyledElement(record.label, className);
          if (record.type === "section") {
            return {
              attrs: { colSpan: this.totalColumnCount },
              children: label,
            };
          }
          return label;
        },
      },
    ];

    for (let index = 0; index < this.dataSource.length; index++) {
      const period = this.dataSource[index];
      columns.push({
        title: this.formatPeriodTitle(period.dateFrom, period.dateTo),
        dataIndex: `period_${index}`,
        key: `period_${index}`,
        customHeaderCell: () => ({ class: "cash-flow-report__th--lg" }),
        customCell: (record: CashFlowReportTableRow) =>
          ROW_CONFIG.footerTypes.includes(
            record.type as "cashflow" | "opening-balance" | "closing-balance"
          )
            ? { class: "cash-flow-report__cell--footer" }
            : {},
        customRender: (text: string, record: CashFlowReportTableRow) => {
          if (record.type === "section") {
            return { attrs: { colSpan: 0, fontSize: "20" } };
          }
          if (
            ROW_CONFIG.footerTypes.includes(
              record.type as "cashflow" | "opening-balance" | "closing-balance"
            )
          ) {
            return this.createStyledElement(
              text != null ? this.formatAmount(text) : "-",
              "cash-flow-report__footer-amount"
            );
          }
          return this.createClickableAmount(text, record, period);
        },
      });
    }

    if (this.showTotal && this.dataSource.length >= 2) {
      columns.push({
        title: this.$t("lbl_total").toString(),
        dataIndex: "period_total",
        key: "period_total",
        customHeaderCell: () => ({ class: "cash-flow-report__th--lg" }),
        customCell: (record: CashFlowReportTableRow) =>
          ROW_CONFIG.footerTypes.includes(
            record.type as "cashflow" | "opening-balance" | "closing-balance"
          )
            ? { class: "cash-flow-report__cell--footer" }
            : {},
        customRender: (text: string, record: CashFlowReportTableRow) => {
          if (record.type === "section") {
            return { attrs: { colSpan: 0 } };
          }
          if (
            record.type === "opening-balance" ||
            record.type === "closing-balance"
          ) {
            return this.createStyledElement(
              "-",
              "cash-flow-report__amount cash-flow-report__amount--default"
            );
          }
          return this.createStyledElement(
            text != null ? this.formatAmount(text) : "-",
            "cash-flow-report__total-amount"
          );
        },
      });
    }

    return columns;
  }

  get tableRows(): CashFlowReportTableRow[] {
    if (!this.dataSource.length) return [];

    const rows: CashFlowReportTableRow[] = [];
    const sections: Array<"OPERATING" | "INVESTING" | "FINANCING"> = [
      "OPERATING",
      "INVESTING",
      "FINANCING",
    ];

    for (const sectionType of sections) {
      rows.push(this.buildSectionHeaderRow(sectionType));
      rows.push(...this.buildSectionRows(sectionType));
      rows.push(this.buildSubtotalRow(sectionType));
    }

    rows.push(
      this.buildFooterRow(
        "cashflow",
        this.$t("lbl_net_increase_decrease_cash_equivalent").toString()
      )
    );
    rows.push(
      this.buildFooterRow(
        "opening-balance",
        this.$t("lbl_opening_balance_period").toString()
      )
    );
    rows.push(
      this.buildFooterRow(
        "closing-balance",
        this.$t("lbl_closing_balance_period").toString()
      )
    );

    return rows;
  }

  private formatPeriodTitle(dateFrom: string, dateTo: string): string {
    return `${this.moment(dateFrom).format(this.defaultDateFormat)} - ${this.moment(
      dateTo
    ).format(this.defaultDateFormat)}`;
  }

  private getCategoryNamesForSection(sectionType: string): Set<string> {
    const categoryNames = new Set<string>();
    for (const col of this.dataSource) {
      const section = col.sections.find(
        (s: CashFlowSectionResult) => s.section === sectionType
      );
      if (section) {
        for (const cat of section.categories) {
          categoryNames.add(cat.name);
        }
      }
    }
    return categoryNames;
  }

  private getCategoryAmount(
    period: CashFlowColumnResult,
    sectionType: string,
    categoryName: string
  ): number | null {
    const section = period.sections.find(
      (s: CashFlowSectionResult) => s.section === sectionType
    );
    if (!section) return null;

    const category = section.categories.find(
      (cat: CashFlowCategoryAmount) => cat.name === categoryName
    );
    return category ? category.amount : null;
  }

  private getSectionLabel(sectionType: string): string {
    const map = {
      OPERATING: "lbl_cash_flow_from_operating_activities",
      INVESTING: "lbl_cash_flow_from_investing_activities",
      FINANCING: "lbl_cash_flow_from_financing_activities",
    };
    return (
      this.$t(map[sectionType] || "").toString() ||
      `Cash Flow From ${this.activityLabels[sectionType]}`
    );
  }

  private getSubtotalLabel(sectionType: string): string {
    return this.$t("lbl_net_cash_provided_by_activities", {
      activity: this.activityLabels[sectionType],
    }).toString();
  }

  private buildRow<T extends CashFlowReportTableRow>(
    key: string,
    type: string,
    label: string,
    valueSelector?: (period: CashFlowColumnResult) => number | null
  ): T {
    const row = { key, type, rowType: type, label } as T;
    for (let idx = 0; idx < this.dataSource.length; idx++) {
      row[`period_${idx}`] = valueSelector
        ? valueSelector(this.dataSource[idx])
        : null;
    }
    if (this.showTotal) {
      row["period_total"] =
        type === "opening-balance" || type === "closing-balance"
          ? undefined
          : (this.sumPeriodValues(row) ?? undefined);
    }
    return row;
  }

  private buildSectionHeaderRow(
    sectionType: "OPERATING" | "INVESTING" | "FINANCING"
  ): CashFlowReportTableRow {
    return this.buildRow(
      `section-${sectionType}`,
      "section",
      this.getSectionLabel(sectionType)
    );
  }

  private buildSubtotalRow(
    sectionType: "OPERATING" | "INVESTING" | "FINANCING"
  ): CashFlowReportTableRow {
    return this.buildRow(
      `subtotal-${sectionType}`,
      "subtotal",
      this.getSubtotalLabel(sectionType),
      period =>
        period.sections.find(s => s.section === sectionType)?.subtotal ?? null
    );
  }

  private buildFooterRow(
    type: "cashflow" | "opening-balance" | "closing-balance",
    label: string
  ): CashFlowReportTableRow {
    const valueMap = {
      cashflow: "netCashFlow",
      "opening-balance": "openingBalance",
      "closing-balance": "closingBalance",
    } as const;
    return this.buildRow(type, type, label, period => period[valueMap[type]]);
  }

  private buildSectionRows(
    sectionType: "OPERATING" | "INVESTING" | "FINANCING"
  ): CashFlowReportSectionTableRow[] {
    if (!this.dataSource[0]) {
      return [];
    }

    const rows: CashFlowReportSectionTableRow[] = [];
    const categoryNames = this.getCategoryNamesForSection(sectionType);

    const firstSection = this.dataSource[0].sections.find(
      s => s.section === sectionType
    );

    for (const categoryName of categoryNames) {
      const category = firstSection?.categories.find(
        c => c.name === categoryName
      );

      const row: CashFlowReportSectionTableRow = {
        key: `${sectionType}-${categoryName}`,
        type: "category",
        rowType: "category",
        categoryId: category?.id || "",
        label: categoryName,
      };

      for (let idx = 0; idx < this.dataSource.length; idx++) {
        const amount = this.getCategoryAmount(
          this.dataSource[idx],
          sectionType,
          categoryName
        );
        row[`period_${idx}`] = amount ?? undefined;
      }

      if (this.showTotal) {
        row["period_total"] = this.sumPeriodValues(row) ?? undefined;
      }

      rows.push(row);
    }

    return rows;
  }
}
